import React, { useEffect, useState } from "react";
import { Form, Modal, Table } from "react-bootstrap";
import InputGroup from "react-bootstrap/InputGroup";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import axios from ".././axios-interceptor";
import {
  base_url,
  LOAN_CURRENCY_TYPE_USD,
  OPPORTUNITY_STATUS_DISBURSING,
  formatMoney,
} from "./../utilities";

// Same options as the legacy LoanDisbursement / SingleInvestorPayments modals.
export const RECEIVED_VIA_NAMES = [
  "Wire Transfer",
  "Paid from Bank Account",
  "Cash",
  "Cheque",
  "Others",
];

const MAX_FILE_SIZE = 5242880;

// Legacy rule (SingleInvestorDetails.jsx): payments may be dated up to tomorrow before noon,
// otherwise up to today.
const computeMaxDate = () => {
  const now = new Date();
  if (now.getHours() < 12) {
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  }
  return now.toISOString().split("T")[0];
};

const useInvestorNames = (investor_request_id, show) => {
  const [breakdown, setBreakdown] = useState(null);
  useEffect(() => {
    if (!show || !investor_request_id) return;
    setBreakdown(null);
    axios
      .post(base_url + "loan-investor-subscriptions/get_breakdown", { investor_request_id })
      .then((res) => {
        if (res?.data?.status === 200) setBreakdown(res.data.data);
      })
      .catch((err) => console.log(err));
  }, [investor_request_id, show]);
  return breakdown;
};

// QA #43: the investor(s) on the loan by name ("Investor Two", or "2 investors: A, B"), not a count.
const investorsLabel = (breakdown, fallbackCount) => {
  const names = [...new Set((breakdown?.investors || []).filter((i) => i.state !== "withdrawn").map((i) => i.investor_name).filter(Boolean))];
  if (names.length === 1) return names[0];
  if (names.length > 1) return `${names.length} investors: ${names.join(", ")}`;
  return fallbackCount ?? "-";
};

const nameFor = (breakdown, split) => {
  const inv = breakdown?.investors?.find(
    (i) => String(i.subscription_id) === String(split.subscription_id)
  );
  if (!inv) return String(split.investor_id || "-");
  return inv.investor_name
    ? `${inv.investor_name}${inv.investor_reference_no ? " (" + inv.investor_reference_no + ")" : ""}`
    : inv.investor_reference_no || String(split.investor_id);
};

const SplitsTable = ({ splits, breakdown, currency, title }) => {
  const total = (splits || []).reduce((s, x) => s + Number(x.amount || 0), 0);
  return (
    <div className="mt-3">
      <h6 style={{ fontWeight: 700, fontSize: 13.5 }}>{title}</h6>
      <Table striped className="bg-white text-center mb-0">
        <thead className="all_inv_table_head">
          <tr>
            <th>Investor</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(splits || []).map((s) => (
            <tr key={String(s.subscription_id)} className="new_inv_interst_table">
              <td>{nameFor(breakdown, s)}</td>
              <td>{formatMoney(s.amount, currency)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ fontWeight: 700 }}>Total</td>
            <td style={{ fontWeight: 700 }}>{formatMoney(total, currency)}</td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

// Payout rows are stored in USD (a BHD tenure is converted with the loan's own exchange rate
// before it is split). Show the split back in the loan currency, like the Investor Breakdown and
// My Investments do. The ratio of the tenure's loan-currency amount to the USD total is exactly
// that rate, so no rate needs to be fetched. Falls back to the stored USD rows when it can't.
const splitsInLoanCurrency = (splits, tenure, loanCurrency) => {
  if (!Array.isArray(splits) || splits.length === 0 || !tenure || loanCurrency === LOAN_CURRENCY_TYPE_USD) return splits;
  const usdTotal = splits.reduce((s, x) => s + Number(x.amount || 0), 0);
  const loanTotal = Number(tenure.tenure_amount_investor_roi);
  if (!(usdTotal > 0) || !(loanTotal > 0)) return splits;
  const out = splits.map((x) => ({ ...x, amount: Math.round(Number(x.amount || 0) * (loanTotal / usdTotal) * 100) / 100 }));
  // Keep the total exact: put any rounding cent on the largest share.
  const drift = Math.round((loanTotal - out.reduce((s, x) => s + x.amount, 0)) * 100) / 100;
  if (drift !== 0) {
    const big = out.reduce((a, b) => (b.amount > a.amount ? b : a));
    big.amount = Math.round((big.amount + drift) * 100) / 100;
  }
  return out;
};

const RadioRow = ({ name, value, onChange, disabled }) => (
  <div className=" p-0 d-flex justify-content-between w-75 flex-wrap">
    {RECEIVED_VIA_NAMES.map((element) => (
      <div key={element} className="admin_PD_radio_div row">
        <label htmlFor={name + element}>
          <input
            className="PD_input_radio "
            id={name + element}
            type="radio"
            value={element}
            name={name}
            checked={value === element}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="kyc_style_doc ps-2">{element}</span>
        </label>
      </div>
    ))}
  </div>
);

const Field = ({ label, children }) => (
  <div className="mb-3">
    <p className="text_st ">{label}</p>
    <span className="bolder_text ">{children}</span>
  </div>
);

const warnBox = {
  background: "#FAEEDA",
  border: "1px solid #FAC775",
  color: "#854F0B",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 12.5,
  marginBottom: 12,
};

const okBox = {
  background: "#EAF3DE",
  border: "1px solid #C0DD97",
  color: "#3B6D11",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 12.5,
  marginTop: 12,
};

/* ------------------------------------------------------------------ */
/* Disburse to business                                               */
/* ------------------------------------------------------------------ */
export const MultiInvestorDisburseModal = ({ show, row, onHide, onDone, canWrite }) => {
  const [paidDate, setPaidDate] = useState("");
  const [paidVia, setPaidVia] = useState("");
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attempted, setAttempted] = useState(false);
  // Repayment schedule, so the admin can re-date tenures before disbursing (funding can take weeks
  // after the schedule was generated at push-to-investors). Same UX/endpoint as legacy LoanDisbursement.
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesFailed, setSchedulesFailed] = useState(false);
  const breakdown = useInvestorNames(row?.investor_request_id, show);

  const loadSchedules = (loan_id) => {
    if (!loan_id) return;
    setSchedules([]);
    setSchedulesFailed(false);
    setSchedulesLoading(true);
    axios
      .post(base_url + "payment-schedules/get_tenure_dates", { loan_id })
      .then((res) => {
        if (res?.data?.status === 200 && Array.isArray(res.data.data)) {
          setSchedules(
            res.data.data.map((t) => ({ ...t, original_tenure_date: t.tenure_date, is_selected: false }))
          );
        } else {
          setSchedulesFailed(true);
        }
        setSchedulesLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setSchedulesFailed(true);
        setSchedulesLoading(false);
      });
  };

  useEffect(() => {
    if (show) {
      setPaidDate("");
      setPaidVia("");
      setComments("");
      setSubmitting(false);
      setResult(null);
      setAttempted(false);
      loadSchedules(row?.loan_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, row?.investor_request_id]);

  if (!row) return null;
  const isResume = row.status === OPPORTUNITY_STATUS_DISBURSING;
  const blocked = !!row.edit_in_progress || !!breakdown?.edit_in_progress || Number(row.pending_withdrawals) > 0;
  const changedSchedules = schedules.filter(
    (t) => moment(t.tenure_date).valueOf() !== moment(t.original_tenure_date).valueOf()
  );
  const scheduleLocked = !!result || submitting || !canWrite;

  const onPenClick = (id) => {
    if (scheduleLocked) return;
    setSchedules((prev) =>
      prev.map((t) => ({ ...t, is_selected: String(t.payment_schedule_id) === String(id) ? !t.is_selected : false }))
    );
  };

  const handleDateChange = (date, index) => {
    if (!date) return;
    // Same encoding as legacy LoanDisbursement: epoch milliseconds at whole seconds.
    const tenure_date = parseInt(Math.round(new Date(date).getTime() / 1000) + "000");
    setSchedules((prev) => prev.map((t, i) => (i === index ? { ...t, tenure_date, is_selected: false } : t)));
  };

  const disburse = () =>
    axios
      .post(base_url + "loan-investor-subscriptions/disburse_to_business", {
        investor_request_id: row.investor_request_id,
        business_id: row.business_id,
        total_paid_amount: row.target_amount,
        paid_currency: row.currency,
        paid_date: paidDate,
        paid_via: paidVia,
        comments: comments,
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          toast.success(res.data.message || "Disbursed to business", { toastId: 340 });
          setResult(res.data.data || {});
          // Dates are now saved; further edits go through a fresh open of the modal.
          setSchedules((prev) => prev.map((t) => ({ ...t, original_tenure_date: t.tenure_date, is_selected: false })));
        }
        setSubmitting(false);
      });

  const handleClose = () => {
    if (submitting) return;
    // Refresh after any submit attempt: a failed run (500 partway, 409 lost claim) can still
    // have changed server state, e.g. leaving the opportunity at DISBURSING.
    if (result || attempted) onDone();
    onHide();
  };

  const submitted = () => {
    if (submitting || result) return;
    if (!paidDate) {
      toast.error("Please select the paid date", { toastId: 344 });
      return;
    }
    if (!paidVia) {
      toast.error("Please select how the payment was processed", { toastId: 345 });
      return;
    }
    setSubmitting(true);
    setAttempted(true);
    // Save re-dated tenures first (only the rows that changed; update_payment_schedule never
    // answers an empty list), so a failed schedule save never leaves a disbursed loan on stale dates.
    const saveSchedule =
      changedSchedules.length > 0
        ? axios
            .post(
              base_url + "payment-schedules/update_payment_schedule",
              changedSchedules.map((t) => ({
                payment_schedule_id: t.payment_schedule_id,
                tenure_amount: t.tenure_amount,
                tenure_date: t.tenure_date,
              })),
              { timeout: 15000 }
            )
            .then((res) => {
              if (res?.data?.status !== 200) return false;
              setSchedules((prev) => prev.map((t) => ({ ...t, original_tenure_date: t.tenure_date })));
              return true;
            })
        : Promise.resolve(true);
    saveSchedule
      .then((ok) => {
        if (!ok) {
          toast.error("Could not save the re-dated schedule — disbursement was not started.", { toastId: 369 });
          setSubmitting(false);
          return;
        }
        return disburse();
      })
      .catch((err) => {
        console.log(err);
        toast.error("Could not save the re-dated schedule — disbursement was not started.", { toastId: 369 });
        setSubmitting(false);
      });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-190w"
      size="lg"
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton className="modal_title">
        <Modal.Title>
          <div className="heading_new_loan ps-2">
            <h5 className="mb-0">{isResume ? "Resume disbursement" : "Disburse to business"}</h5>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-2" style={{ height: "28rem", overflow: "auto" }}>
        {isResume && (
          <div style={warnBox}>
            A previous disbursement run for this loan was interrupted. Saving again resumes it;
            investors already disbursed are skipped and never paid twice.
          </div>
        )}
        {(row.edit_in_progress || breakdown?.edit_in_progress) && (
          <div style={warnBox}>An investor breakdown edit is in progress. Wait for it to finish before disbursing.</div>
        )}
        {Number(row.pending_withdrawals) > 0 && (
          <div style={warnBox}>
            {row.pending_withdrawals} pending withdrawal request(s) on this loan. Reject them in the Pending
            Withdrawals panel before disbursing.
          </div>
        )}
        <div className="row">
          <div className="col">
            <Field label="Loan Reference No.">{row.loan_reference_number || "-"}</Field>
          </div>
          <div className="col">
            <Field label="Business">{row.business_name || "-"}</Field>
          </div>
          <div className="col">
            <Field label="Investors">{investorsLabel(breakdown, row.investor_count)}</Field>
          </div>
        </div>

        <div className="contianer p-0 d-flex">
          <div className="form-group mb-3 me-3 custm_input_styl">
            <label htmlFor="mi_disb_paid_date">
              <p>
                Paid Date <span style={{ color: "red" }}>*</span>
              </p>
            </label>
            <input
              id="mi_disb_paid_date"
              type="date"
              disabled={!!result || submitting}
              className="form-control rounded custm_border payemnt_details_BS_form_conrtl"
              onChange={(e) => {
                const d = Date.parse(e.target.value);
                setPaidDate(isNaN(d) ? "" : d);
              }}
            />
          </div>
          <div className="form-group mb-3 ">
            <label htmlFor="mi_disb_amount">
              <p>Paid Amount</p>
            </label>
            <InputGroup className="mb-3 payemnt_details_BS_form_conrtl">
              <Form.Control
                id="mi_disb_amount"
                type="text"
                className="rounded shadow-ben px-3 amt_field_border"
                disabled
                readOnly
                value={Number(row.target_amount).toLocaleString()}
              />
              <InputGroup.Text className="paymnt_details_amt ">
                <p className="Payment_currency">{row.currency === LOAN_CURRENCY_TYPE_USD ? "$" : "BHD"}</p>
              </InputGroup.Text>
            </InputGroup>
          </div>
        </div>

        <div>
          <h3>
            Processed Via <span style={{ color: "red" }}>*</span>
          </h3>
          <RadioRow name="mi_disb_via" value={paidVia} onChange={setPaidVia} disabled={!!result || submitting} />
        </div>

        <div>
          <h3 className="mt-4">Comments</h3>
          <Form.Control
            size="sm"
            className="ANLtextarea "
            as="textarea"
            style={{ height: "50px", width: "95%" }}
            rows={2}
            disabled={!!result || submitting}
            placeholder="Please enter comments..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>

        <div className="mt-3">
          <h6 style={{ fontWeight: 700, fontSize: 13.5 }}>Repayment schedule</h6>
          <p style={{ fontSize: 11.5, color: "#7C8495", marginBottom: 6 }}>
            Tenure dates are recalculated from the disbursement (paid) date when you disburse, and cannot be edited.
          </p>
          {schedulesLoading ? (
            <div style={{ fontSize: 12.5, color: "#7C8495" }}>Loading schedule…</div>
          ) : schedulesFailed ? (
            <div style={warnBox}>
              Could not load the repayment schedule.{" "}
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ fontSize: 12.5 }}
                onClick={() => loadSchedules(row.loan_id)}
              >
                Retry
              </button>
            </div>
          ) : schedules.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "#7C8495" }}>No open tenures found.</div>
          ) : (
            <Table striped className="bg-white text-center mb-0">
              <thead className="all_inv_table_head">
                <tr>
                  <th>Profit Amount Payment</th>
                  <th>Tenure Date (before disbursement)</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((t, index) => {
                  const changed = moment(t.tenure_date).valueOf() !== moment(t.original_tenure_date).valueOf();
                  return (
                    <tr key={String(t.payment_schedule_id)} className="new_inv_interst_table">
                      <td>{formatMoney(t.tenure_amount, t.admin_request_loan_currency || row.currency)}</td>
                      <td>
                        {t.is_selected && !scheduleLocked ? (
                          <DatePicker
                            className="width_date_picker"
                            showIcon
                            onChange={(d) => handleDateChange(d, index)}
                            selected={new Date(moment(t.tenure_date).format("YYYY/MM/DD"))}
                          />
                        ) : (
                          <span style={changed ? { color: "#854F0B", fontWeight: 700 } : undefined}>
                            {moment(t.tenure_date).format("DD MMM YYYY")}
                            {changed ? " (changed)" : ""}
                          </span>
                        )}
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>

        {!result && breakdown?.investors?.length > 0 && (
          <div className="mt-3">
            <h6 style={{ fontWeight: 700, fontSize: 13.5 }}>Investors on this loan</h6>
            <Table striped className="bg-white text-center mb-0">
              <thead className="all_inv_table_head">
                <tr>
                  <th>Investor</th>
                  <th>Committed</th>
                  <th>Share</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.investors.map((inv) => (
                  <tr key={String(inv.subscription_id)} className="new_inv_interst_table">
                    <td>{inv.investor_name || inv.investor_reference_no || "-"}</td>
                    <td>{formatMoney(inv.subscribed_amount, inv.currency || row.currency)}</td>
                    <td>{inv.share_percent}%</td>
                    <td>{inv.state}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {result && (
          <>
            <div style={okBox}>
              Disbursement recorded.
              {Array.isArray(result.skipped_already_disbursed) && result.skipped_already_disbursed.length > 0
                ? ` ${result.skipped_already_disbursed.length} investor(s) had already been disbursed in an earlier run and were skipped.`
                : ""}
            </div>
            <SplitsTable
              title="Per-investor split"
              splits={result.splits}
              breakdown={breakdown}
              currency={row.currency}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex ps-3 pe-3">
        <div>
          <button className="btn btn_background mx-2 px-3 py-2" onClick={handleClose} disabled={submitting}>
            Close
          </button>
          {!result && canWrite && (
            <button
              className="btn blue_btn mx-3 px-4 py-2"
              disabled={submitting || blocked || !paidDate || !paidVia || schedulesLoading}
              onClick={submitted}
            >
              {submitting ? "Saving..." : isResume ? "Resume disbursement" : "Disburse"}
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* Pay one tenure to every investor                                   */
/* ------------------------------------------------------------------ */
export const MultiInvestorPayTenureModal = ({ show, row, onHide, onDone, canWrite }) => {
  const [tenures, setTenures] = useState([]);
  const [tenuresLoading, setTenuresLoading] = useState(false);
  const [scheduleId, setScheduleId] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [receivedVia, setReceivedVia] = useState("");
  const [comments, setComments] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [maxDate] = useState(computeMaxDate);
  const breakdown = useInvestorNames(row?.investor_request_id, show);

  useEffect(() => {
    if (!show || !row?.loan_id) return;
    setScheduleId("");
    setPaidDate("");
    setReceivedVia("");
    setComments("");
    setFile(null);
    setSubmitting(false);
    setResult(null);
    setAttempted(false);
    setTenures([]);
    setTenuresLoading(true);
    axios
      .post(base_url + "payment-schedules/get_tenure_dates_for_investor", { loan_id: row.loan_id })
      .then((res) => {
        if (res?.data?.status === 200) setTenures(res.data.data || []);
        setTenuresLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setTenuresLoading(false);
      });
  }, [show, row?.loan_id]);

  if (!row) return null;
  const selected = tenures.find((t) => String(t.payment_schedule_id) === String(scheduleId));
  const paidCurrency = selected?.admin_request_loan_currency || row.currency;

  const handleClose = () => {
    if (submitting) return;
    // Refresh after any submit attempt: a failed run (500 partway, 409 lost claim) can still
    // have changed server state, e.g. leaving the opportunity at DISBURSING.
    if (result || attempted) onDone();
    onHide();
  };

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size >= MAX_FILE_SIZE) {
      toast.error("File size should be less than 5MB", { toastId: 346 });
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submitted = () => {
    if (submitting || result) return;
    if (!selected) {
      toast.error("Please select a tenure", { toastId: 348 });
      return;
    }
    if (!paidDate) {
      toast.error("Please select the paid date", { toastId: 344 });
      return;
    }
    if (!receivedVia) {
      toast.error("Please select how the payment was made", { toastId: 345 });
      return;
    }
    let body;
    if (file) {
      body = new FormData();
      body.append("payment_schedule_id", selected.payment_schedule_id);
      body.append("paid_currency", paidCurrency);
      body.append("paid_date", paidDate);
      body.append("received_via", receivedVia);
      body.append("comments", comments);
      // Legacy convention: the backend builds the stored name from file.name, which the
      // legacy modal sets to the extension only.
      const lastDot = file.name.lastIndexOf(".");
      body.append("file", file, file.name.substring(lastDot + 1));
    } else {
      body = {
        payment_schedule_id: selected.payment_schedule_id,
        paid_currency: paidCurrency,
        paid_date: paidDate,
        received_via: receivedVia,
        comments: comments,
      };
    }
    setSubmitting(true);
    setAttempted(true);
    axios
      .post(base_url + "loan-investor-subscriptions/pay_tenure_to_investors", body)
      .then((res) => {
        if (res?.data?.status === 200) {
          toast.success(res.data.message || "Tenure paid out to investors", { toastId: 341 });
          setResult(res.data.data || {});
        }
        setSubmitting(false);
      })
      .catch((err) => {
        console.log(err);
        setSubmitting(false);
      });
  };

  const formLocked = !!result || submitting;
  const shownSplits = result ? splitsInLoanCurrency(result.splits, selected, paidCurrency) : null;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-190w"
      size="lg"
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton className="modal_title">
        <Modal.Title>
          <div className="heading_new_loan ps-2">
            <h5 className="mb-0">Pay tenure to investors</h5>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-2" style={{ height: "28rem", overflow: "auto" }}>
        <div className="row">
          <div className="col">
            <Field label="Loan Reference No.">{row.loan_reference_number || "-"}</Field>
          </div>
          <div className="col">
            <Field label="Business">{row.business_name || "-"}</Field>
          </div>
          <div className="col">
            <Field label="Investors">{investorsLabel(breakdown, row.investor_count)}</Field>
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="mi_tenure_select">
            <p>
              Tenure <span style={{ color: "red" }}>*</span>
            </p>
          </label>
          {tenuresLoading ? (
            <p className="text_st">Loading tenures...</p>
          ) : tenures.length === 0 ? (
            <div style={warnBox}>
              No tenure is ready for payout. A tenure is listed once the business repayment for it has
              been recorded and verified, and until it has been paid out to investors.
            </div>
          ) : (
            <Form.Select
              id="mi_tenure_select"
              className="payemnt_details_BS_form_conrtl"
              value={scheduleId}
              disabled={formLocked}
              onChange={(e) => setScheduleId(e.target.value)}
            >
              <option value="">Select</option>
              {tenures.map((t) => (
                <option key={String(t.payment_schedule_id)} value={String(t.payment_schedule_id)}>
                  {moment(t.tenure_date).format("DD MMM YYYY")} —{" "}
                  {formatMoney(t.tenure_amount_investor_roi, t.admin_request_loan_currency || row.currency)}
                </option>
              ))}
            </Form.Select>
          )}
        </div>

        <div className="contianer p-0 d-flex">
          <div className="form-group mb-3 me-3 custm_input_styl">
            <label htmlFor="mi_tenure_paid_date">
              <p>
                Paid Date <span style={{ color: "red" }}>*</span>
              </p>
            </label>
            <input
              id="mi_tenure_paid_date"
              type="date"
              max={maxDate}
              disabled={formLocked}
              className="form-control rounded custm_border payemnt_details_BS_form_conrtl"
              onChange={(e) => {
                const d = Date.parse(e.target.value);
                setPaidDate(isNaN(d) ? "" : d);
              }}
            />
          </div>
          <div className="form-group mb-3 ">
            <label htmlFor="mi_tenure_amount">
              <p>Total Amount</p>
            </label>
            <InputGroup className="mb-3 payemnt_details_BS_form_conrtl">
              <Form.Control
                id="mi_tenure_amount"
                type="text"
                className="rounded shadow-ben px-3 amt_field_border"
                disabled
                readOnly
                value={selected ? Number(selected.tenure_amount_investor_roi).toLocaleString() : ""}
              />
              <InputGroup.Text className="paymnt_details_amt ">
                <p className="Payment_currency">{paidCurrency === LOAN_CURRENCY_TYPE_USD ? "$" : "BHD"}</p>
              </InputGroup.Text>
            </InputGroup>
          </div>
        </div>

        <div>
          <h3>
            Received Via <span style={{ color: "red" }}>*</span>
          </h3>
          <RadioRow name="mi_tenure_via" value={receivedVia} onChange={setReceivedVia} disabled={formLocked} />
        </div>

        <div>
          <h3 className="mt-4">Comments</h3>
          <Form.Control
            size="sm"
            className="ANLtextarea "
            as="textarea"
            style={{ height: "50px", width: "95%" }}
            rows={2}
            disabled={formLocked}
            placeholder="Please enter comments..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>

        <div className="mt-3">
          <h3>Proof of payment (optional, max 5MB)</h3>
          <input type="file" className="form-control" disabled={formLocked} onChange={handleFile} />
        </div>

        {result && (
          <>
            <div style={okBox}>
              Tenure paid out to all investors.
              {result.investor_side_fully_repaid ? " All tenures are now paid out to investors." : ""}
            </div>
            <SplitsTable
              title="Per-investor split"
              splits={shownSplits}
              breakdown={breakdown}
              currency={shownSplits === result.splits ? LOAN_CURRENCY_TYPE_USD : paidCurrency}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex ps-3 pe-3">
        <div>
          <button className="btn btn_background mx-2 px-3 py-2" onClick={handleClose} disabled={submitting}>
            Close
          </button>
          {!result && canWrite && (
            <button
              className="btn blue_btn mx-3 px-4 py-2"
              disabled={submitting || !selected || !paidDate || !receivedVia}
              onClick={submitted}
            >
              {submitting ? "Saving..." : "Pay investors"}
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* Approve / reject a withdrawal request                              */
/* ------------------------------------------------------------------ */
export const MultiInvestorWithdrawalReviewModal = ({ show, row, mode, onHide, onDone, canWrite }) => {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setNotes("");
      setSubmitting(false);
    }
  }, [show, row?.subscription_id, mode]);

  if (!row) return null;
  const isReject = mode === "reject";

  const handleClose = () => {
    if (submitting) return;
    onHide();
  };

  const submitted = () => {
    if (submitting) return;
    if (isReject && !notes.trim()) {
      toast.error("A reason is required to reject a withdrawal request", { toastId: 347 });
      return;
    }
    setSubmitting(true);
    axios
      .post(
        base_url + (isReject ? "loan-investor-subscriptions/reject_withdrawal" : "loan-investor-subscriptions/approve_withdrawal"),
        { subscription_id: row.subscription_id, review_notes: notes.trim() }
      )
      .then((res) => {
        if (res?.data?.status === 200) {
          toast.success(res.data.message || (isReject ? "Withdrawal rejected" : "Withdrawal approved"), {
            toastId: isReject ? 343 : 342,
          });
          setSubmitting(false);
          onHide();
          onDone();
        } else {
          // The request may have been handled elsewhere or the loan filled; refresh the lists.
          setSubmitting(false);
          onDone();
        }
      })
      .catch((err) => {
        console.log(err);
        setSubmitting(false);
        onDone();
      });
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton className="modal_title">
        <Modal.Title>
          <div className="heading_new_loan ps-2">
            <h5 className="mb-0">{isReject ? "Reject withdrawal" : "Approve withdrawal"}</h5>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        <Field label="Investor">
          {row.investor_name || "-"}
          {row.investor_reference_no ? ` (${row.investor_reference_no})` : ""}
        </Field>
        <Field label="Loan Reference No.">{row.loan_reference_number || "-"}</Field>
        <Field label="Amount">{formatMoney(row.subscribed_amount, row.currency)}</Field>
        {row.withdrawal_reason ? <Field label="Investor's reason">{row.withdrawal_reason}</Field> : null}
        {!isReject && (
          <p className="text_st" style={{ fontWeight: 400 }}>
            Approving releases the pledge back to the investor's available balance and frees this amount on the
            opportunity.
          </p>
        )}
        <h3 className="mt-2">
          {isReject ? "Reason" : "Notes (optional)"} {isReject && <span style={{ color: "red" }}>*</span>}
        </h3>
        <Form.Control
          size="sm"
          className="ANLtextarea "
          as="textarea"
          rows={3}
          style={{ width: "100%" }}
          placeholder={isReject ? "Why is this request rejected?" : "Notes for the record..."}
          value={notes}
          disabled={submitting}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer className="d-flex ps-3 pe-3">
        <div>
          <button className="btn btn_background mx-2 px-3 py-2" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          {canWrite && (
            <button
              className="btn mx-3 px-4 py-2"
              style={{ backgroundColor: isReject ? "#ED5151" : "#059669", color: "white" }}
              disabled={submitting || (isReject && !notes.trim())}
              onClick={submitted}
            >
              {submitting ? "Saving..." : isReject ? "Reject" : "Approve"}
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};
